import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  CheckCircle2,
  ChevronLeft,
  Eye,
  Keyboard,
  Lightbulb,
  Shield,
} from 'lucide-react';

import { CaptionCard } from '../components/annotation/CaptionCard.jsx';
import { CaptionSwitcher } from '../components/annotation/CaptionSwitcher.jsx';
import { AdminWorkspace } from '../components/annotation/admin/AdminWorkspace.jsx';
import { AnnotationToolbar } from '../components/annotation/admin/AnnotationToolbar.jsx';
import { ImagePane } from '../components/annotation/ImagePane.jsx';
import { TaskNavigator } from '../components/annotation/TaskNavigator.jsx';
import { ShortcutCheatsheet } from '../components/annotation/ShortcutCheatsheet.jsx';
import { AdminExtras } from '../components/annotation/AdminExtras.jsx';
import { Keycap, SaveStatus, Spinner } from '../components/common/index.jsx';
import { useDebouncedSave } from '../hooks/useDebouncedSave.js';
import { useHotkeys } from '../hooks/useHotkeys.js';
import { ApiError, admin, work } from '../lib/api.js';
import { SPAN_TYPES, spanProblem } from '../lib/spans.js';
import { useAuthStore } from '../store/authStore.js';
import { useEditorStore } from '../store/editorStore.js';
import { useUiStore } from '../store/uiStore.js';

export function AnnotationPage() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const workspaceRef = useRef(null);

  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'admin';

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [conflict, setConflict] = useState(null);
  // Admin toolbar: the corpus index it navigates with.
  const [corpus, setCorpus] = useState(null);
  const [reviewFlag, setReviewFlag] = useState('all');
  const [queue, setQueue] = useState([]);
  // Filled only when submitting finds unfinished tags.
  const [submitGaps, setSubmitGaps] = useState([]);
  // Admins can view the page exactly as an annotator receives it. The server builds
  // that payload, so the preview exercises the real stripping and caption shuffle
  // rather than a UI imitation of them.
  const [previewAnnotator, setPreviewAnnotator] = useState(false);
  const asAnnotator = isAdmin && previewAnnotator;

  const cheatsheetOpen = useUiStore((s) => s.cheatsheetOpen);
  const toggleCheatsheet = useUiStore((s) => s.toggleCheatsheet);
  const closeCheatsheet = useUiStore((s) => s.closeCheatsheet);
  const toast = useUiStore((s) => s.toast);

  const drafts = useEditorStore((s) => s.drafts);
  const focusedSlot = useEditorStore((s) => s.focusedSlot);
  const current = drafts[focusedSlot] || null;
  const focusSlot = useEditorStore((s) => s.focusSlot);
  const load = useEditorStore((s) => s.load);
  const reset = useEditorStore((s) => s.reset);

  const ownerParam = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('owner') || undefined;
  }, [taskId]);

  /* --- saving ------------------------------------------------------------------ */

  const save = useCallback(async () => {
    const store = useEditorStore.getState();
    // While previewing, save exactly as an annotator would - no decision arrays - so
    // the preview exercises the annotator's write path too, not just its read path.
    const payload = store.buildPayload(isAdmin && !asAnnotator);
    if (!payload.length) return;

    try {
      const response = await work.patchTask(store.taskId, payload, ownerParam);
      store.markSaved(response.results || []);
      setConflict(null);
    } catch (err) {
      if (err instanceof ApiError && err.isConflict) {
        // Someone else changed this while we were typing. Never silently clobber.
        setConflict(err.details);
      } else if (err instanceof ApiError && err.isValidation) {
        // A half-typed tag is the normal state a second after it is drawn, and the
        // server no longer rejects those. Anything that still fails here is a real
        // structural problem (a bad offset, an overlap), so it is worth interrupting
        // for; unfinished tags are flagged inline and collected at submit instead.
        toast(err.message, { tone: 'danger', timeout: 8000 });
      }
      throw err;
    }
  }, [ownerParam, toast, isAdmin, asAnnotator]);

  const saveState = useDebouncedSave(save, { delay: 800, maxWait: 5000 });

  // Queue a save whenever any draft becomes dirty.
  useEffect(
    () =>
      useEditorStore.subscribe((state, previous) => {
        if (state.drafts === previous.drafts) return;
        if (state.drafts.some((d) => d.dirty)) saveState.queue(Date.now());
      }),
    [saveState],
  );

  /* --- loading ------------------------------------------------------------------ */

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    work
      .task(taskId, ownerParam, previewAnnotator ? 'annotator' : undefined)
      .then((payload) => {
        if (cancelled) return;
        setTask(payload);
        load(payload, payload.owner_user_id);
        // Prefetch the next image so Shift+Enter lands on a rendered page.
        for (const id of [payload.next_unfinished_task_id, payload.next_task_id]) {
          if (id) {
            work.task(id, ownerParam, previewAnnotator ? 'annotator' : undefined).then((next) => {
              if (!next.task.image_missing) new Image().src = next.task.image_url;
            }).catch(() => {});
          }
        }
      })
      .catch((err) => !cancelled && setError(err))
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
      // Flush before leaving so navigating away never loses work.
      saveState.flush();
      reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, ownerParam, previewAnnotator]);


  // The admin toolbar needs the dataset/image/caption index. Annotators never see it,
  // so it is only fetched for admins.
  useEffect(() => {
    if (!isAdmin || !task) return;
    admin
      .pipelineIndex({ dataset: task.task.dataset_key, review_flag: reviewFlag })
      .then(setCorpus)
      .catch(() => setCorpus(null));
  }, [isAdmin, task?.task?.dataset_key, reviewFlag, drafts]);

  const goTo = useCallback(
    async (nextId) => {
      if (!nextId) {
        toast('Nothing left in your queue', { tone: 'ok' });
        return;
      }
      await saveState.flush();
      navigate(`/annotate/${nextId}${ownerParam ? `?owner=${ownerParam}` : ''}`);
    },
    [navigate, ownerParam, saveState, toast],
  );

  /**
   * Jump to another dataset's first image.
   *
   * The corpus index is fetched filtered to the dataset currently open, so it can never
   * contain an image from the one being switched to - searching it was the bug that made
   * these buttons do nothing. Ask the server for the target dataset instead; the effect
   * above refetches the index once the new task loads.
   */
  const switchDataset = useCallback(
    async (key) => {
      if (!key || key === task?.task?.dataset_key) return;
      try {
        const next = await admin.pipelineIndex({ dataset: key, review_flag: reviewFlag });
        const first = next.images?.[0];
        if (!first) {
          toast('That dataset has no images matching the current filter', { tone: 'danger' });
          return;
        }
        goTo(first.task_id);
      } catch {
        toast('Could not open that dataset', { tone: 'danger' });
      }
    },
    // Must be declared AFTER `goTo`: a dependency array is evaluated during render, so
    // naming a `const` from further down throws before the page can paint.
    [task?.task?.dataset_key, reviewFlag, toast, goTo],
  );

  /**
   * Leave the workspace, flushing any pending save first.
   *
   * Autosave is debounced, so navigating away on the keystroke after an edit would drop
   * it. Everywhere else that leaves this page does the same - see `goTo`.
   */
  const leaveWorkspace = useCallback(
    async (to) => {
      await saveState.flush();
      navigate(to || (isAdmin ? '/admin' : '/queue'));
    },
    [navigate, saveState, isAdmin],
  );

  // The jump list: an annotator navigates their assigned queue, an admin the corpus.
  useEffect(() => {
    work
      .queue()
      .then((r) =>
        setQueue(
          (r.items || []).map((i) => ({
            task_id: i.task_id,
            image_id: i.image_id,
            done: i.status === 'submitted' || i.status === 'approved',
            started: i.samples_submitted > 0 || i.status === 'in_progress',
          })),
        ),
      )
      .catch(() => setQueue([]));
  }, [taskId]);

  const navItems = useMemo(() => {
    if (queue.length) return queue;
    // An admin with nothing assigned still needs to move around the corpus.
    return (corpus?.images || []).map((i) => ({
      task_id: i.task_id,
      image_id: i.image_id,
      done: i.captions.every((c) => c.is_reviewed),
      started: i.captions.some((c) => c.is_reviewed),
    }));
  }, [queue, corpus]);

  const navPosition = useMemo(() => {
    const idx = navItems.findIndex((i) => i.task_id === Number(taskId));
    return idx >= 0 ? idx + 1 : task?.position ?? null;
  }, [navItems, taskId, task]);

  const goRelative = useCallback(
    (where) => {
      if (typeof where === 'number') return goTo(where);
      const idx = navItems.findIndex((i) => i.task_id === Number(taskId));
      if (idx < 0) return goTo(where === 'prev' ? task?.prev_task_id : task?.next_task_id);
      const next = navItems[idx + (where === 'next' ? 1 : -1)];
      return goTo(next?.task_id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navItems, taskId, task],
  );

  const goNextUnfinished = useCallback(() => {
    const idx = navItems.findIndex((i) => i.task_id === Number(taskId));
    const after = navItems.slice(idx + 1).find((i) => !i.done);
    const before = navItems.slice(0, Math.max(0, idx)).find((i) => !i.done);
    const target = after || before;
    if (!target) {
      toast('Everything in your queue is done', { tone: 'ok' });
      return;
    }
    goTo(target.task_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navItems, taskId, goTo, toast]);

  /** The old tab's "Clear": wipe this caption's annotation back to the agent text. */
  const clearCaption = useCallback((slot) => {
    const store = useEditorStore.getState();
    const target = typeof slot === 'number' ? slot : store.focusedSlot;
    const draft = store.drafts[target];
    if (!draft) return;
    if (
      !window.confirm(
        `Clear caption ${draft.label}?\n\nThis removes every tag and undoes your caption ` +
          `edits, restoring the original agent caption. It cannot be undone.`,
      )
    ) {
      return;
    }
    store.updateDraft(target, {
      working: draft.agentCaption,
      finalCaption: draft.agentCaption,
      spans: [],
      spanDecisions: [],
      detailDecisions: [],
      notes: '',
      remapReport: null,
      undoSnapshot: null,
    });
    toast('Caption cleared', { tone: 'ok' });
  }, [toast]);

  const markDoneAndAdvance = useCallback(async () => {
    await saveState.flush();

    // Check locally first, so the annotator gets the whole list in one go rather than
    // a round trip per problem.
    const gaps = useEditorStore
      .getState()
      .drafts.flatMap((draft) =>
        draft.spans
          .map((span) => ({ span, problem: spanProblem(span) }))
          .filter(({ problem }) => problem)
          .map(({ span, problem }) => `caption ${draft.label}: “${span.text.slice(0, 36)}” — ${problem}`),
      );

    if (gaps.length) {
      setSubmitGaps(gaps);
      return;
    }

    try {
      await work.submitTask(taskId);
      setSubmitGaps([]);
      toast('Image marked done', { tone: 'ok' });
      goTo(task?.next_unfinished_task_id || task?.next_task_id);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'incomplete_spans') {
        setSubmitGaps(
          (err.details?.spans || []).map(
            (s) => `“${String(s.text).slice(0, 36)}” — needs a ${s.missing}`,
          ),
        );
        return;
      }
      toast(err.message || 'Could not mark the image done', { tone: 'danger' });
    }
  }, [saveState, taskId, task, toast, goTo]);

  /* --- shortcuts ---------------------------------------------------------------- */

  const bindings = useMemo(() => {
    const store = () => useEditorStore.getState();
    const map = {
      '1': () => store().focusSlot(0),
      '2': () => store().focusSlot(1),
      '3': () => store().focusSlot(2),
      Tab: () => store().cycleFocus(1),
      'shift+Tab': () => store().cycleFocus(-1),
      // Arrows move between IMAGES - the most frequent navigation by far, so it gets
      // the most reachable keys. Tags are walked with j/k.
      ArrowLeft: () => goRelative('prev'),
      ArrowRight: () => goRelative('next'),
      // Up/Down are deliberately NOT bound: capturing them would break scrolling the
      // page with the keyboard. Tags are walked with j/k.
      j: () => store().moveCursor(1),
      k: () => store().moveCursor(-1),
      Escape: () => {
        // Esc clears first and only leaves once there is nothing left to clear, so it
        // can never navigate away mid-tag.
        const s = store();
        if (s.selection || s.spanCursor >= 0) {
          s.setSelection(null);
          s.setCursor(-1);
          return;
        }
        leaveWorkspace();
      },
      d: () => withCursor((i) => store().updateSpan(i, { operation: 'remove' })),
      r: () => withCursor((i) => store().updateSpan(i, { operation: 'replace' })),
      Backspace: () => withCursor((i) => store().removeSpan(i)),
      Delete: () => withCursor((i) => store().removeSpan(i)),
      '[': () => goRelative('prev'),
      ']': () => goRelative('next'),
      'shift+Enter': markDoneAndAdvance,
      'mod+s': { run: () => saveState.flush(), allowInInput: true },
      '?': () => toggleCheatsheet(),
      g: () => leaveWorkspace('/guidelines'),
    };

    // h / u / w tag the selection.
    for (const label of SPAN_TYPES) {
      const key = label === 'hallucinated' ? 'h' : label === 'uncertain' ? 'u' : 'w';
      map[key] = () => {
        if (!store().selection) return;
        if (store().addTag(label) === null) {
          toast('That selection overlaps an existing tag', { tone: 'danger' });
        }
      };
    }
    return map;

    function withCursor(fn) {
      const { spanCursor } = store();
      if (spanCursor >= 0) fn(spanCursor);
    }
  }, [task, goTo, goRelative, leaveWorkspace, markDoneAndAdvance, saveState, toggleCheatsheet, toast]);

  // Bound to the document, NOT to the workspace element. A scoped binding only fires
  // when focus is already inside that element, so arrow keys did nothing until you
  // happened to click inside the page. `useHotkeys` still guards against firing while
  // the user is typing, which is what the scoping was really protecting.
  useHotkeys(bindings, { enabled: !loading && !cheatsheetOpen });

  /* --- render -------------------------------------------------------------------- */

  if (loading) return <Spinner label="Loading image…" />;
  if (error) {
    return (
      <div className="page">
        <div className="banner danger">
          {error.status === 403
            ? 'This image is not assigned to you.'
            : error.message || 'Could not load this image.'}
        </div>
      </div>
    );
  }
  if (!task) return null;

  const done = task.assignment?.status === 'submitted' || task.assignment?.status === 'approved';

  return (
    <div ref={workspaceRef} tabIndex={-1} style={{ outline: 'none' }}>
      <header className="topbar">
        {/* The workspace hides the sidebar to give the caption room, so this is the only
            way back. It saves first: leaving with a pending debounce would silently drop
            the last edit. */}
        <button
          type="button"
          className="btn ghost sm back-out"
          onClick={() => leaveWorkspace()}
          title={`Back to ${isAdmin ? 'the dashboard' : 'your queue'} (Esc)`}
        >
          <ChevronLeft size={14} /> {isAdmin ? 'Dashboard' : 'My queue'}
        </button>

        <TaskNavigator
          position={navPosition}
          total={navItems.length || task.queue_total}
          items={navItems}
          currentTaskId={Number(taskId)}
          onGo={goRelative}
          onNextUnfinished={goNextUnfinished}
          hasPrev={navPosition ? navPosition > 1 : Boolean(task.prev_task_id)}
          hasNext={navPosition ? navPosition < navItems.length : Boolean(task.next_task_id)}
        />
        <span className="topbar-meta">
          {task.task.family === 'coco' ? 'COCO' : 'Web'} · {task.task.image_id}
        </span>
        {done && <span className="chip ok">done</span>}
        <span className="spacer" />
        <SaveStatus state={saveState} onRetry={saveState.retry} />
        {isAdmin && (
          <button
            type="button"
            className={`btn sm ${asAnnotator ? 'primary' : 'ghost'}`}
            title={
              asAnnotator
                ? 'You are seeing exactly what an annotator receives. Click to return to the admin view.'
                : 'See this page exactly as an annotator does'
            }
            onClick={() => setPreviewAnnotator((v) => !v)}
          >
            {asAnnotator ? <Shield size={14} /> : <Eye size={14} />}
            {asAnnotator ? 'Back to admin view' : 'Annotator view'}
          </button>
        )}
        <button
          type="button"
          className="btn ghost sm"
          onClick={() => leaveWorkspace('/examples')}
          title="Finished annotations to compare against"
        >
          <Lightbulb size={14} /> Examples
        </button>
        <button
          type="button"
          className="btn ghost sm"
          onClick={() => leaveWorkspace('/guidelines')}
        >
          <BookOpen size={14} /> Guidelines
        </button>
        <button type="button" className="btn ghost sm" onClick={toggleCheatsheet} aria-label="Keyboard shortcuts">
          <Keyboard size={14} /> <Keycap>?</Keycap>
        </button>
      </header>

      {asAnnotator && (
        <div className="banner info preview-banner">
          <Eye size={15} />
          <span className="grow">
            <strong>Annotator view.</strong> This is the payload an annotator receives -
            no model names, no original caption, no agent decisions, captions shuffled and
            labelled A/B/C. Anything you save here still saves for real.
          </span>
          <span className="banner-actions">
            <button type="button" className="btn sm" onClick={() => setPreviewAnnotator(false)}>
              Back to admin view
            </button>
          </span>
        </div>
      )}

      {submitGaps.length > 0 && (
        <div className="banner warn preview-banner" role="alert">
          <span className="grow">
            <strong>
              {submitGaps.length} tag{submitGaps.length > 1 ? 's' : ''} still need
              {submitGaps.length > 1 ? '' : 's'} filling in before this image can be marked done:
            </strong>
            <ul style={{ margin: '6px 0 0', paddingLeft: '1.1em' }}>
              {submitGaps.map((gap) => (
                <li key={gap}>{gap}</li>
              ))}
            </ul>
          </span>
          <span className="banner-actions">
            <button type="button" className="btn sm" onClick={() => setSubmitGaps([])}>
              Dismiss
            </button>
          </span>
        </div>
      )}

      {conflict && (
        <div className="banner warn" style={{ margin: 'var(--space-4) var(--space-4) 0' }}>
          <span className="grow">
            <strong>{conflict.last_edited_by}</strong> changed this annotation while you were
            working. Your unsaved edits are still here.
          </span>
          <span className="banner-actions">
            <button type="button" className="btn sm" onClick={() => window.location.reload()}>
              Reload theirs
            </button>
            <button type="button" className="btn sm" onClick={() => setConflict(null)}>
              Keep mine
            </button>
          </span>
        </div>
      )}

      {isAdmin && !asAnnotator && (
        <AnnotationToolbar
          index={corpus}
          dataset={task.task.dataset_key}
          onDataset={switchDataset}
          image={corpus?.images.find((i) => i.task_id === task.task.id) || null}
          onImage={(id) => goTo(id)}
          sampleId={current?.sampleId}
          onSample={(sid) => {
            const slot = drafts.findIndex((d) => d.sampleId === sid);
            if (slot >= 0) focusSlot(slot);
          }}
          reviewFlag={reviewFlag}
          onReviewFlag={setReviewFlag}
          saveState={saveState}
          onRetry={saveState.retry}
        />
      )}

      <div className="workspace">
        <ImagePane task={task.task} showProvenance />

        <div className="caption-stack">
          {isAdmin && !asAnnotator ? (
            current && (
              <>
                <AdminWorkspace
                  key={current.sampleId}
                  sample={task.samples.find((s) => s.sample_id === current.sampleId)}
                  draft={current}
                  slot={focusedSlot}
                  onClear={clearCaption}
                />
                <AdminExtras
                  sample={task.samples.find((s) => s.sample_id === current.sampleId)}
                  draft={current}
                  slot={focusedSlot}
                />
              </>
            )
          ) : (
            <>
              <CaptionSwitcher drafts={drafts} focusedSlot={focusedSlot} onSelect={focusSlot} />
              {current && (
                <CaptionCard
                  key={current.sampleId}
                  draft={current}
                  slot={focusedSlot}
                  onClear={clearCaption}
                />
              )}
            </>
          )}

          <div className="row-between" style={{ marginTop: 'var(--space-2)' }}>
            <div className="row gap-1">
              <button
                type="button"
                className="btn"
                disabled={!task.prev_task_id}
                onClick={() => goRelative('prev')}
              >
                <ArrowLeft size={14} /> Previous image <Keycap>←</Keycap>
              </button>
              <button
                type="button"
                className="btn"
                disabled={!task.next_task_id}
                onClick={() => goRelative('next')}
              >
                Next image <Keycap>→</Keycap> <ArrowRight size={14} />
              </button>
            </div>
            <button type="button" className="btn primary lg" onClick={markDoneAndAdvance}>
              <CheckCircle2 size={16} />
              {done ? 'Re-submit and continue' : 'Mark image done'} <Keycap>⇧⏎</Keycap>
            </button>
          </div>
        </div>
      </div>

      {cheatsheetOpen && <ShortcutCheatsheet onClose={closeCheatsheet} />}
    </div>
  );
}
